import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { CustomFieldInput, CustomFieldView, UpdateCustomFieldInput } from '@nest-recruit/shared';
import { ApiError, apiGet, apiPatch, apiPost } from '../../../lib/api-client';
import type { RowCache } from '../components/simple-list/use-row-changes';

// ADMIN-13 (S31 Q19; Admin redesign batch 2). Admin only. A change reaches every record's "More details" too, so their
// fields and values are refetched after one.

export const ADMIN_FIELDS_KEY = ['admin', 'custom-fields'] as const;

export function useAdminCustomFields(isEnabled: boolean): UseQueryResult<readonly CustomFieldView[], ApiError> {
  return useQuery<readonly CustomFieldView[], ApiError>({
    queryKey: ADMIN_FIELDS_KEY,
    queryFn: () => apiGet<readonly CustomFieldView[]>('/admin/custom-fields'),
    enabled: isEnabled,
    placeholderData: (previous) => previous,
  });
}

export const FIELD_ROWS: RowCache<readonly CustomFieldView[], CustomFieldView> = {
  queryKey: ADMIN_FIELDS_KEY,
  rowsOf: (rows) => rows,
  withRows: (_rows, rows) => rows,
  idOf: (row) => row.id,
  alsoRefresh: [['custom-fields'], ['custom-field-values']],
};

function versionOf(rows: readonly CustomFieldView[], id: string): number {
  const row = rows.find((field) => field.id === id);
  if (!row) throw new ApiError({ code: 'not_found', message: "That field doesn't exist any more.", status: 404 });
  return row.version;
}

export const customFieldsApi = {
  change: (id: string, change: Omit<UpdateCustomFieldInput, 'version'>) => async (version: number) => versionOf(await apiPatch<readonly CustomFieldView[]>(`/admin/custom-fields/${id}`, { ...change, version }), id),
  /** The new field is the one of that name on that module the list didn't have before. */
  create: async (input: CustomFieldInput, knownIds: ReadonlySet<string>): Promise<CustomFieldView> => {
    const rows = await apiPost<readonly CustomFieldView[]>('/admin/custom-fields', input);
    const row = rows.find((field) => !knownIds.has(field.id) && field.module === input.module && field.label === input.label);
    if (!row) throw new ApiError({ code: 'not_found', message: 'The field was added, but the list did not come back with it. Reload to see it.', status: 404 });
    return row;
  },
};
